import React, { useEffect, useRef, useState } from 'react';

import ImageModal from '../components/ImageModal';
import { useRuntimeSettings } from '../context/RuntimeSettingsContext';
import {
  formatConfidencePercent,
  formatEta,
  formatStartedAt,
  formatTokenCount,
  normalizeConfidenceScore,
  toBool,
} from '../utils/formatters';
import {
  computeAccuracyRatio,
  getDisabledImageKeySet,
  getLotActionKey,
  getResultImageUrls,
  getResultLotNumber,
  getReviewNoteDraftKey,
  getRunProgressMetrics,
  getTrustedReviewNote,
  normalizeImageKey,
  withRagPromptPreview,
} from '../utils/testRigHelpers';
import { createDataControls } from './logic/dataControls';
import { buildDerivedState, formatCostDisplay, getSourceBadgeClassName } from './logic/derivedState';
import { createLotImageRerunControls } from './logic/lotImageRerunControls';
import { createResultAndNotesControls } from './logic/resultAndNotesControls';
import { createRunControls } from './logic/runControls';
import RunTableSection from './ui/RunTableSection';
import TopControlsSection from './ui/TopControlsSection';

const TestRigPageContainer = () => {
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataLoadError, setDataLoadError] = useState('');
  const [expandedRun, setExpandedRun] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedLotNum, setSelectedLotNum] = useState(null);
  const [selectedItems, setSelectedItems] = useState(null);
  const {
    modelName,
    setModelName,
    resizeMode,
    setResizeMode,
    compressionMode,
    setCompressionMode,
    useEbayRag,
    setUseEbayRag,
    useNotesRag,
    setUseNotesRag,
    assumeHiddenItems,
    setAssumeHiddenItems,
  } = useRuntimeSettings();
  const [isChangingModel, setIsChangingModel] = useState(false);
  const [isChangingResizeMode, setIsChangingResizeMode] = useState(false);
  const [isChangingCompressionMode, setIsChangingCompressionMode] = useState(false);
  const [isChangingEbayRag, setIsChangingEbayRag] = useState(false);
  const [isChangingNotesRag, setIsChangingNotesRag] = useState(false);
  const [isChangingAssumeHidden, setIsChangingAssumeHidden] = useState(false);
  const [codePromptTemplates, setCodePromptTemplates] = useState({
    ebay_off_notes_off: '',
    ebay_on_notes_off: '',
    ebay_off_notes_on: '',
    ebay_on_notes_on: '',
  });
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [datasetSearch, setDatasetSearch] = useState('');
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(false);
  const [isRefreshingDatasets, setIsRefreshingDatasets] = useState(false);
  const [datasetLoadError, setDatasetLoadError] = useState('');
  const [isDatasetDropdownOpen, setIsDatasetDropdownOpen] = useState(false);
  const [runModelSearchQuery, setRunModelSearchQuery] = useState('');
  const [runDatasetSearchQuery, setRunDatasetSearchQuery] = useState('');
  const [runMinConfidencePercentById, setRunMinConfidencePercentById] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [activeRuns, setActiveRuns] = useState([]);
  const [isStartingRun, setIsStartingRun] = useState(false);
  const [completionGraceUntilMs, setCompletionGraceUntilMs] = useState(0);
  const [loadingRunDetailsById, setLoadingRunDetailsById] = useState({});
  const [terminatingRunIds, setTerminatingRunIds] = useState({});
  const [isTerminatingAllRuns, setIsTerminatingAllRuns] = useState(false);
  const [isFlushing, setIsFlushing] = useState(false);
  const [rerunningLotBusy, setRerunningLotBusy] = useState({});
  const [reviewNoteDrafts, setReviewNoteDrafts] = useState({});
  const [savingReviewNotes, setSavingReviewNotes] = useState({});
  const [lotNoteDrafts, setLotNoteDrafts] = useState({});
  const [savingLotNotes, setSavingLotNotes] = useState({});
  const datasetDropdownRef = useRef(null);
  const fetchSequenceRef = useRef(0);
  const latestAppliedFetchSequenceRef = useRef(0);
  const hadStatusActiveRunsRef = useRef(false);
  const COMPLETION_POLL_GRACE_MS = 30000;

  const isRunInProgress = activeRuns.length > 0;
  const completionGraceActive = completionGraceUntilMs > Date.now();
  const shouldPollRunStatus = isRunInProgress || isRunning === true || isStartingRun || completionGraceActive;

  const dataControls = createDataControls({
    setLoading,
    setDataLoadError,
    setTestResults,
    setCompletionGraceUntilMs,
    setActiveRuns,
    setIsRunning,
    setCodePromptTemplates,
    setDatasets,
    setSelectedDataset,
    setDatasetSearch,
    setDatasetLoadError,
    setIsLoadingDatasets,
    setIsRefreshingDatasets,
    setIsDatasetDropdownOpen,
    setModelName,
    setResizeMode,
    setCompressionMode,
    setUseEbayRag,
    setUseNotesRag,
    setAssumeHiddenItems,
    setIsChangingModel,
    setIsChangingResizeMode,
    setIsChangingCompressionMode,
    setIsChangingEbayRag,
    setIsChangingNotesRag,
    setIsChangingAssumeHidden,
    fetchSequenceRef,
    latestAppliedFetchSequenceRef,
    hadStatusActiveRunsRef,
    COMPLETION_POLL_GRACE_MS,
    selectedDataset,
    datasets,
    isRefreshingDatasets,
    datasetSearch,
  });

  const runControls = createRunControls({
    selectedDataset,
    datasets,
    modelName,
    resizeMode,
    compressionMode,
    useEbayRag,
    useNotesRag,
    assumeHiddenItems,
    isStartingRun,
    isRunInProgress,
    isRunning,
    isTerminatingAllRuns,
    isFlushing,
    loadingRunDetailsById,
    testResults,
    expandedRun,
    setIsStartingRun,
    setResizeMode,
    setCompressionMode,
    setUseEbayRag,
    setUseNotesRag,
    setAssumeHiddenItems,
    setSelectedDataset,
    setDatasetSearch,
    setIsRunning,
    setActiveRuns,
    setIsTerminatingAllRuns,
    setIsFlushing,
    setLoadingRunDetailsById,
    setTestResults,
    setExpandedRun,
    setTerminatingRunIds,
    fetchData: dataControls.fetchData,
    fetchPromptTemplates: dataControls.fetchPromptTemplates,
    fetchDatasets: dataControls.fetchDatasets,
  });

  const resultAndNotesControls = createResultAndNotesControls({
    setTestResults,
    setSavingReviewNotes,
    setReviewNoteDrafts,
    setSavingLotNotes,
    setLotNoteDrafts,
    fetchData: dataControls.fetchData,
    mergeUpdatedRunInState: runControls.mergeUpdatedRunInState,
  });

  const lotImageRerunControls = createLotImageRerunControls({
    testResults,
    setRerunningLotBusy,
    updateRunLotResultInState: resultAndNotesControls.updateRunLotResultInState,
    normalizeImageKeyList: resultAndNotesControls.normalizeImageKeyList,
    isExpectedLastImageToggleBlock: resultAndNotesControls.isExpectedLastImageToggleBlock,
    mergeUpdatedRunInState: runControls.mergeUpdatedRunInState,
    fetchData: dataControls.fetchData,
  });

  const getFilteredItemEntries = (items = [], minConfidenceThreshold = 0) => {
    const hasConcreteItems = Array.isArray(items) && items.length > 0;
    const baseEntries = hasConcreteItems
      ? items.map((item, index) => ({ item, originalIndex: index }))
      : [{ item: null, originalIndex: 0 }];
    if (!(minConfidenceThreshold > 0)) return baseEntries;
    return baseEntries.filter(({ item }) => {
      const normalizedConfidence = normalizeConfidenceScore(
        item?.confidence_score ?? item?.confidence,
      );
      return normalizedConfidence !== null && normalizedConfidence >= minConfidenceThreshold;
    });
  };

  const derived = buildDerivedState({
    testResults,
    activeRuns,
    runModelSearchQuery,
    runDatasetSearchQuery,
    runMinConfidencePercentById,
    currentPage,
    itemsPerPage,
    selectedDataset,
    isLoadingDatasets,
    datasetLoadError,
    datasets,
    loading,
    codePromptTemplates,
    useEbayRag,
    useNotesRag,
    assumeHiddenItems,
    getFilteredItemEntries,
  });

  const paginate = (pageNumber) => {
    const nextPage = Math.max(1, Math.min(derived.totalPages, pageNumber));
    setCurrentPage(nextPage);
    setExpandedRun(null);
  };

  const setRunConfidencePercent = (run = {}, runRowKey = '', value = 0) => {
    const runKey = String(run?.run_id || runRowKey || '').trim();
    if (!runKey) return;
    const numericValue = Number(value);
    const clamped = Number.isFinite(numericValue)
      ? Math.max(0, Math.min(100, Math.round(numericValue)))
      : 0;
    setRunMinConfidencePercentById((previous) => ({ ...previous, [runKey]: clamped }));
    const normalizedRunId = String(run?.run_id || '').trim();
    if (clamped > 0 && normalizedRunId && !Array.isArray(run?.results)) {
      runControls.fetchRunDetails(normalizedRunId, { silent: true });
    }
  };

  useEffect(() => {
    dataControls.fetchData({ showLoading: true });
    dataControls.fetchPromptTemplates();
    dataControls.fetchDatasets();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datasetDropdownRef.current && !datasetDropdownRef.current.contains(event.target)) {
        setIsDatasetDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (shouldPollRunStatus) {
        dataControls.fetchData({ showLoading: false });
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [shouldPollRunStatus]);

  useEffect(() => {
    setCurrentPage(1);
  }, [runModelSearchQuery, runDatasetSearchQuery, itemsPerPage]);

  useEffect(() => {
    if (currentPage > derived.totalPages) {
      setCurrentPage(derived.totalPages);
      setExpandedRun(null);
    }
  }, [currentPage, derived.totalPages]);

  const controller = {
    ...dataControls,
    ...runControls,
    ...resultAndNotesControls,
    ...lotImageRerunControls,
    ...derived,
    testResults,
    loading,
    dataLoadError,
    expandedRun,
    selectedImage,
    selectedImages,
    selectedLotNum,
    selectedItems,
    modelName,
    resizeMode,
    compressionMode,
    useEbayRag,
    useNotesRag,
    assumeHiddenItems,
    isChangingModel,
    isChangingResizeMode,
    isChangingCompressionMode,
    isChangingEbayRag,
    isChangingNotesRag,
    isChangingAssumeHidden,
    datasets,
    selectedDataset,
    datasetSearch,
    isLoadingDatasets,
    isRefreshingDatasets,
    datasetLoadError,
    isDatasetDropdownOpen,
    runModelSearchQuery,
    runDatasetSearchQuery,
    runMinConfidencePercentById,
    currentPage,
    itemsPerPage,
    activeRuns,
    isStartingRun,
    loadingRunDetailsById,
    terminatingRunIds,
    isTerminatingAllRuns,
    isFlushing,
    rerunningLotBusy,
    reviewNoteDrafts,
    savingReviewNotes,
    lotNoteDrafts,
    savingLotNotes,
    isRunInProgress,
    datasetDropdownRef,
    setSelectedImage,
    setSelectedImages,
    setSelectedLotNum,
    setSelectedItems,
    setSelectedDataset,
    setDatasetSearch,
    setIsDatasetDropdownOpen,
    setRunModelSearchQuery,
    setRunDatasetSearchQuery,
    setExpandedRun,
    setCurrentPage,
    setItemsPerPage,
    setReviewNoteDrafts,
    setLotNoteDrafts,
    setRunConfidencePercent,
    getFilteredItemEntries,
    paginate,
    toBool,
    formatStartedAt,
    formatEta,
    formatTokenCount,
    formatConfidencePercent,
    withRagPromptPreview,
    computeAccuracyRatio,
    getResultLotNumber,
    getResultImageUrls,
    getDisabledImageKeySet,
    getLotActionKey,
    getReviewNoteDraftKey,
    getTrustedReviewNote,
    normalizeImageKey,
    getRunProgressMetrics,
    getSourceBadgeClassName,
    formatCostDisplay,
  };

  return (
    <div className="w-full max-w-none p-3 sm:p-6 bg-surface rounded-lg shadow-md">
      <TopControlsSection controller={controller} />
      <RunTableSection controller={controller} />
      <ImageModal
        isOpen={!!selectedImage}
        onClose={() => {
          setSelectedImage(null);
          setSelectedImages([]);
          setSelectedLotNum(null);
          setSelectedItems(null);
        }}
        imageUrl={selectedImage}
        imageUrls={selectedImages}
        lotNum={selectedLotNum}
        items={selectedItems}
      />
    </div>
  );
};

export default TestRigPageContainer;
